import React from 'react';
import styled from 'styled-components';
import { Info } from 'lucide-react';

// Styled Components
const SectionContainer = styled.div`
  margin-bottom: 32px;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
`;

const SectionTitle = styled.h3`
  font-size: ${props => props.theme.textLg};
  font-weight: ${props => props.theme.fontSemibold};
  color: ${props => props.theme.foreground};
  margin: 0;
`;

const InfoIcon = styled(Info)`
  width: 16px;
  height: 16px;
  color: ${props => props.theme.mutedForeground};
  cursor: help;
`;

const StyledSettingItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 0;
  border-bottom: 1px solid ${props => props.theme.border};
  
  &:last-child {
    border-bottom: none;
  }
`;

const SettingInfo = styled.div`
  flex: 1;
`;

const SettingTitle = styled.div`
  font-size: ${props => props.theme.textSm};
  font-weight: ${props => props.theme.fontMedium};
  color: ${props => props.theme.foreground};
  margin-bottom: 4px;
`;

const SettingDescription = styled.div`
  font-size: ${props => props.theme.textXs};
  color: ${props => props.theme.mutedForeground};
  line-height: 1.4;
`;

const SettingAction = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ToggleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ToggleLabel = styled.span`
  font-size: ${props => props.theme.textSm};
  color: ${props => props.theme.foreground};
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.primary};
  font-size: ${props => props.theme.textSm};
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: all 0.2s ease;
  
  &:hover {
    background: ${props => props.theme.primary}10;
  }
`;

const StatusBar = styled.div<{ $variant: 'warning' | 'info' | 'success' }>`
  padding: 12px 16px;
  border-radius: 8px;
  margin-top: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: ${props => props.theme.textSm};
  
  ${props => {
    switch (props.$variant) {
      case 'warning':
        return `
          background: ${props.theme.destructive}10;
          color: ${props.theme.destructive};
          border: 1px solid ${props.theme.destructive}20;
        `;
      case 'info':
        return `
          background: ${props.theme.primary}10;
          color: ${props.theme.primary};
          border: 1px solid ${props.theme.primary}20;
        `;
      case 'success':
        return `
          background: ${props.theme.success}10;
          color: ${props.theme.success};
          border: 1px solid ${props.theme.success}20;
        `;
      default:
        return '';
    }
  }}
`;

const InfoText = styled.div`
  font-size: ${props => props.theme.textXs};
  color: ${props => props.theme.mutedForeground};
  margin-top: 16px;
  line-height: 1.4;
  
  a {
    color: ${props => props.theme.primary};
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

// Types
interface SettingItemProps {
  title: string;
  description: string;
  action?: React.ReactNode;
  showInfo?: boolean;
}

interface SettingsSectionProps {
  title: string;
  showInfo?: boolean;
  children: React.ReactNode;
  statusBar?: {
    variant: 'warning' | 'info' | 'success';
    message: string;
    icon?: React.ReactNode;
  };
  infoText?: string;
}

// Components
export const SettingItem: React.FC<SettingItemProps> = ({ 
  title, 
  description, 
  action, 
  showInfo = false 
}) => {
  return (
    <StyledSettingItem>
      <SettingInfo>
        <SettingTitle>{title}</SettingTitle>
        <SettingDescription>{description}</SettingDescription>
      </SettingInfo>
      {action && <SettingAction>{action}</SettingAction>}
    </StyledSettingItem>
  );
};

export const SettingsSection: React.FC<SettingsSectionProps> = ({ 
  title, 
  showInfo = false, 
  children, 
  statusBar,
  infoText 
}) => {
  return (
    <SectionContainer>
      <SectionHeader>
        <SectionTitle>{title}</SectionTitle>
        {showInfo && <InfoIcon />}
      </SectionHeader>
      {children}
      {statusBar && (
        <StatusBar $variant={statusBar.variant}>
          {statusBar.icon}
          {statusBar.message}
        </StatusBar>
      )}
      {infoText && (
        <InfoText dangerouslySetInnerHTML={{ __html: infoText }} />
      )}
    </SectionContainer>
  );
};

export { ToggleContainer, ToggleLabel, ActionButton };
